import { displayDate } from './dates';

/**
 * How the escrow screens print a deadline date and order a deal's timeline (ESC1-UI, his BUILD 2026-10-03).
 *
 * ⛔⛔ The year is on EVERY date: the 1101 E. 1st St deal runs 2025 to 2027, and "Mar 22" (2027) beside
 *    "Mar 23" (2026) could not be told apart. Only the first ten characters are read - the day the screens
 *    always showed - and through `displayDate`, so a calendar date never prints as the day before.
 *    A value it cannot read gives null, so the screen shows its own "Set date" (the column is a DATE, so only
 *    a hand-edited row could get here; the old formatter printed "Invalid Date").
 */
export const deadlineDate = (value: string | null | undefined): string | null =>
  value ? displayDate(value.slice(0, 10), { month: 'short', day: 'numeric', year: 'numeric' }) : null;

/**
 * DOCTRACK part 2 (design 4.5): what an event row's date was counted from - "counted from Nov 1, 2026, when it arrived
 * by email". Every escrow date prints with its year.
 */
export const countedFromWords = (eventDate: string, source: 'email' | 'filed' | 'manual'): string =>
  `counted from ${deadlineDate(eventDate)}, ${source === 'email' ? 'when it arrived by email' : source === 'filed' ? 'when it was filed' : 'the date entered by hand'}`;

export interface DatedRow { id: string; label: string; due_date: string | null; sort_order?: number | null }

/**
 * ⛔⛔ Date order, oldest first, undated last. The API lists a deal's rows by `sort_order` (the contract's
 *    0-5, then every added row at 999), which put the 1101 deal's 2025 history below its 2027 closing.
 *    On one day the contract's sequence decides, then the label (the desk's rows carry no sort_order).
 */
export const byDueDate = (a: DatedRow, b: DatedRow): number => {
  const da = a.due_date?.slice(0, 10) ?? '';
  const db = b.due_date?.slice(0, 10) ?? '';
  if (da !== db) return !da ? 1 : !db ? -1 : da < db ? -1 : 1;
  const sa = a.sort_order ?? Number.MAX_SAFE_INTEGER;
  const sb = b.sort_order ?? Number.MAX_SAFE_INTEGER;
  if (sa !== sb) return sa - sb;
  return a.label.localeCompare(b.label, 'en-US') || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
};

/**
 * ⭐ ESC1-TZ (the 117th): an escrow day is a PACIFIC day. Today's calendar date in Los Angeles as YYYY-MM-DD -
 *    never UTC, which is already tomorrow from 5 PM PT (4 PM in winter). The backend counts the same way.
 */
export const pacificToday = (nowMs: number = Date.now()): string => {
  // Assembled from its PARTS - never a locale's format string, which a browser release can change (the review).
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Los_Angeles', year: 'numeric', month: '2-digit', day: '2-digit' })
    .formatToParts(new Date(nowMs));
  const part = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
};

/**
 * ⭐ ESC1-MARK (the 118th): NOW as the Pacific wall clock, in ms on the same axis as a date read at UTC midnight
 *    (`Date.parse('YYYY-MM-DD' + 'T00:00:00Z')`) - so a timeline's "Today" stands on the Pacific day, not tomorrow
 *    from 5 PM PT. Built from its parts, like `pacificToday`; `h23` so midnight is 0, never 24.
 */
export const pacificWallMs = (nowMs: number = Date.now()): number => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles', hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(new Date(nowMs));
  const n = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  return Date.UTC(n('year'), n('month') - 1, n('day'), n('hour'), n('minute'), n('second'));
};

/** Whole Pacific days from today to the due date: 0 on the day, negative once it has passed, null when undated. */
export const daysUntilDue = (due: string | null, nowMs: number = Date.now()): number | null => {
  if (!due) return null;
  const d = Date.parse(`${due.slice(0, 10)}T00:00:00Z`);
  return Math.round((d - Date.parse(`${pacificToday(nowMs)}T00:00:00Z`)) / 86400000);
};

/**
 * ⭐ TASK-RED (the 118th): `deal_tasks.due_date` is a TIMESTAMP, and every writer stores a PACIFIC calendar date - the
 *    task form's date box at midnight UTC, the checklist and the GHL pull through `checklistDueDate` / `pacificTaskDate`
 *    (backend `escrowDeadlines.ts`; before the review they stored a UTC moment and a UTC date), the June templates at
 *    12:33 PM PT - so the stored value's first ten characters ARE the date. Read as a moment it is 5 PM PT the day before.
 */
export const taskDueDate = (due: string | null | undefined): string | null => (due ? due.slice(0, 10) : null);

/** Overdue only once its Pacific due date has passed - the rule the escrow countdowns already use. */
export const isTaskOverdue = (due: string | null | undefined, isCompleted: boolean | null | undefined, nowMs: number = Date.now()): boolean => {
  if (isCompleted) return false;
  const left = daysUntilDue(taskDueDate(due), nowMs);
  return left !== null && left < 0;
};
